import { type SelectHTMLAttributes, forwardRef, useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Field, controlClasses } from './field';

export interface NativeSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  sizeVariant?: 'sm' | 'md';
  wrapperClassName?: string;
}

/** Native `<select>` styled to the control spec; the chevron follows `currentColor`. */
export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(
  (
    {
      className,
      wrapperClassName,
      label,
      error,
      helperText,
      sizeVariant = 'md',
      children,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = props.id ?? generatedId;
    const descriptionId = error || helperText ? `${id}-description` : undefined;

    return (
      <Field
        id={id}
        label={label}
        error={error}
        helperText={helperText}
        descriptionId={descriptionId}
        className={wrapperClassName}
      >
        <div className="relative">
          <select
            ref={ref}
            id={id}
            aria-invalid={error ? true : undefined}
            aria-describedby={descriptionId}
            className={cn(
              controlClasses,
              'appearance-none bg-none! pe-9',
              sizeVariant === 'sm' ? 'h-11 ps-2.5 text-[13px] lg:h-8' : 'h-11 ps-3 lg:h-9',
              className
            )}
            {...props}
          >
            {children}
          </select>
          <ChevronDown
            className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-ink-2"
            aria-hidden="true"
          />
        </div>
      </Field>
    );
  }
);

NativeSelect.displayName = 'NativeSelect';
